'use client';

import { CSSProperties, MouseEvent, forwardRef } from 'react';
import { Env } from '@/libs/env';

interface IconFontProps {
  /** Icon name (e.g., 'logo_apple' or 'icon-logo_apple') */
  name: string;
  /** Icon size in pixels (optional, defaults to 16px) */
  size?: number | string;
  /** Icon color (optional, defaults to text-secondary) */
  color?: string;
  /** Additional CSS classes */
  className?: string;
  /** Inline styles */
  style?: CSSProperties;
  /** Click event handler */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
}

// Debug mode: only available in development, completely removed in production builds
const DEBUG_ICONS = process.env.NODE_ENV !== 'production' && Env.NEXT_PUBLIC_DEBUG_ICONS === 'true';

/**
 * Iconfont Icon Component (支持 ref 转发,可与 Radix UI asChild 配合使用)
 *
 * @example
 * ```tsx
 * // Basic usage
 * <IconFont name="logo_apple" />
 *
 * // With size and color
 * <IconFont name="bangzhu-28x28" size={24} color="#00BBFF" />
 *
 * // With custom className
 * <IconFont name="icon-logo_apple" className="mr-2" />
 *
 * // With click handler
 * <IconFont name="logo_apple" onClick={() => console.log('clicked')} />
 *
 * // With ref (for Radix UI asChild)
 * <PopoverTrigger asChild>
 *   <IconFont name="help" ref={iconRef} />
 * </PopoverTrigger>
 * ```
 */
const IconFont = forwardRef<HTMLElement, IconFontProps>(({
  name,
  size = 16,
  color,
  className = '',
  style,
  onClick,
}, ref) => {
  // Add 'icon-' prefix if not present
  const iconName = name.startsWith('icon-') ? name : `icon-${name}`;

  const iconStyle: CSSProperties = {
    ...style,
    fontSize: typeof size === 'number' ? `${size}px` : size,
    ...(color && { color }),
    // Debug mode: visual indicators without affecting layout
    ...(DEBUG_ICONS && {
      outline: '1px solid #00ff00',
      outlineOffset: '1px',
    }),
  };

  return (
    <i
      ref={ref}
      className={`iconfont ${iconName} text-secondary ${className} leading-none`}
      style={iconStyle}
      onClick={onClick}
      aria-hidden="true"
    />
  );
});

IconFont.displayName = 'IconFont';

export default IconFont;
